import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useMemo,
  useEffect
} from 'react'
import type { RetorikActivity } from '../../models/activityTypes'
import type { Filter } from '../../models/filterTypes'
import { hooks } from 'botframework-webchat-component'
import { getColorFromCategoryId } from '../../utils/getColorFromCategoryId'
import { useView } from './ViewContext'

const { useSendPostBack } = hooks

export type ListProviderProps = {
  children?: ReactNode
}

export type ListContextType = {
  content: RetorikActivity | undefined
  filters: Filter[]
  selectedFilters: Filter[]
  color: string
  setContent: (x: RetorikActivity | undefined) => void
  setSelectedFilters: (x: Filter[]) => void
  resetFilters: () => void
  processFiltersOnChange: (x: Filter[], y?: boolean) => void
  setSuggestedFilter: (x: Filter) => void
}

const ListContextDefaultValues: ListContextType = {
  content: undefined,
  filters: [],
  selectedFilters: [],
  color: '',
  setContent: () => {},
  setSelectedFilters: () => {},
  resetFilters: () => {},
  processFiltersOnChange: () => {},
  setSuggestedFilter: () => {}
}

export const ListContext = createContext<ListContextType>(
  ListContextDefaultValues
)

export function useList(): ListContextType {
  return useContext(ListContext)
}

export function ListProvider({ children }: ListProviderProps): JSX.Element {
  const { themeColors } = useView()
  const [content, setContent] = useState<RetorikActivity | undefined>()
  const [filters, setFilters] = useState<Filter[]>([])
  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])
  const color = useMemo(() => {
    return getColorFromCategoryId(
      content?.channelData?.category?.id,
      themeColors
    )
  }, [content])
  const sendPostBack = useSendPostBack()

  const setSuggestedFilter = (filter: Filter): void => {
    setSelectedFilters([filter])
  }

  /**
   * On call :
   *  - set selectedFilters state to empty array
   *  - send postBack message with empty filters
   */
  const resetFilters = (): void => {
    setSelectedFilters([])
    sendPostBack({ filters: [] })
  }

  /**
   * On call :
   *  - set selectedFilters and filters states to empty arrays
   */
  const totalReset = (): void => {
    setSelectedFilters([])
    setFilters([])
  }

  const processFiltersOnChange = (data: Filter[], full = false): void => {
    let tempFilters: Filter[] = []

    if (selectedFilters.length > 0) {
      let remainingData = [...data]
      // Check on each selected filter to modify if necessary
      selectedFilters.forEach((selectedFilter) => {
        let items: string[] = [...selectedFilter.items]
        data.forEach((filter) => {
          if (filter.id === selectedFilter.id) {
            // Special treatment for calendar filter : only 1 value allowed
            if (filter.id === 'Event.DateRange') {
              if (
                items.length > 0 &&
                filter.items.length > 0 &&
                items[0] === filter.items[0]
              ) {
                items = []
              } else {
                items = [...filter.items]
              }
            } else {
              // If the chosen filter is a full one and has already at least one item chosen, let's choose all its items
              if (full) {
                items =
                  selectedFilter.items.length === filter.items.length
                    ? []
                    : [...filter.items]
              }
              // Else let's deal with the items one by one
              else {
                items = [...selectedFilter.items, ...filter.items].filter(
                  (item) =>
                    !(
                      selectedFilter.items.includes(item) &&
                      filter.items.includes(item)
                    )
                )
              }
            }

            // Let's remove this filter from remainingData because it has been processed
            remainingData = remainingData.filter((fil) => fil.id !== filter.id)
          }
        })

        items.length > 0 &&
          (tempFilters = [
            ...tempFilters,
            { id: selectedFilter.id, title: selectedFilter.title, items: items }
          ])
      })

      // If some filters weren't in the selected filters, we add them to the selected filters
      remainingData.length > 0 &&
        (tempFilters = [...tempFilters, ...remainingData])
    } else {
      data.length > 0 && (tempFilters = data)
    }

    setSelectedFilters(tempFilters)
    sendPostBack({ filters: tempFilters })
  }

  const value = useMemo(
    () => ({
      content: content,
      filters: filters,
      selectedFilters: selectedFilters,
      color: color,
      setContent,
      setSelectedFilters,
      resetFilters,
      processFiltersOnChange,
      setSuggestedFilter
    }),
    [
      content,
      filters,
      selectedFilters,
      color,
      setContent,
      setSelectedFilters,
      resetFilters,
      processFiltersOnChange,
      setSuggestedFilter
    ]
  )

  /**
   * On content state change :
   *  - if there is no content, reset all filters and selected filters
   *  - if there is some content, set filters state with the content received or an empty array, and selectedFilters state with received data if there are some
   */
  useEffect(() => {
    if (content) {
      if (filters.length === 0 || content.channelData?.filters !== filters) {
        setFilters(content.channelData?.filters || [])
      }
      content.channelData?.selectedFilters &&
        setSelectedFilters(content.channelData?.selectedFilters)
    } else {
      totalReset()
    }
  }, [content])

  return (
    <React.Fragment>
      <ListContext.Provider value={value}>{children}</ListContext.Provider>
    </React.Fragment>
  )
}
